// Shared "I Need Numbers" 45-second hype video modal.
// Controlled component — parent owns open state. Radix Dialog handles
// outside-click / Escape / body-scroll-lock / focus trap for free.
//
// Usage:
//   const [open, setOpen] = useState(false);
//   <button onClick={() => setOpen(true)}>Play</button>
//   <INNHypeVideoModal open={open} onOpenChange={setOpen} />
//
// Also exports <INNHypeVideoThumb /> — a poster-image-with-play-overlay
// button you can drop anywhere to trigger the modal.

import React, { useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

export const INN_HYPE_VIDEO_SRC =
  'https://customer-assets.emergentagent.com/job_e8e571d7-8951-428c-943c-5304042e2db3/artifacts/t53zev11_inn-hype.mp4';
export const INN_HYPE_POSTER_SRC =
  'https://customer-assets.emergentagent.com/job_e8e571d7-8951-428c-943c-5304042e2db3/artifacts/tpsy28kl_inn-hype-poster.jpg';

const SCOPED_CSS = `
  @keyframes innVideoFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @media (max-width: 639px) {
    .inn-video-shell {
      max-width: 100% !important;
      border-radius: 0 !important;
      height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .inn-video-shell video {
      border-radius: 0 !important;
      max-height: 100vh !important;
    }
    .inn-video-shell [data-testid="inn-hype-video-close-btn"] {
      top: max(12px, env(safe-area-inset-top, 12px)) !important;
      right: 12px !important;
    }
  }
  .inn-video-thumb {
    position: relative;
    display: inline-block;
    cursor: pointer;
    background: #000;
    border: 0;
    padding: 0;
    overflow: hidden;
    font-family: inherit;
    transition: transform 200ms ease, box-shadow 200ms ease;
  }
  .inn-video-thumb:hover { transform: translateY(-2px); }
  .inn-video-thumb:hover .inn-video-play { transform: translate(-50%, -50%) scale(1.08); }
  .inn-video-thumb:focus-visible { outline: 3px solid #a78bfa; outline-offset: 2px; }
  .inn-video-play {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 64px; height: 64px;
    border-radius: 50%;
    background: rgba(255,255,255,0.95);
    box-shadow: 0 6px 24px rgba(0,0,0,0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 200ms ease;
    pointer-events: none;
  }
  .inn-video-play svg { margin-left: 4px; }
  .inn-video-duration-pill {
    position: absolute;
    bottom: 10px; right: 10px;
    background: rgba(0,0,0,0.75);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 4px;
    letter-spacing: 0.02em;
    pointer-events: none;
  }
`;

export function INNHypeVideoModal({ open, onOpenChange, onEnded }) {
  const videoRef = useRef(null);

  // Pause when modal closes — some Android browsers keep audio running.
  useEffect(() => {
    if (!open && videoRef.current) {
      try { videoRef.current.pause(); } catch (_) { /* noop */ }
    }
  }, [open]);

  return (
    <>
      <style>{SCOPED_CSS}</style>
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              zIndex: 100,
              animation: 'innVideoFadeIn 180ms ease-out',
            }}
          />
          <Dialog.Content
            aria-describedby={undefined}
            data-testid="inn-hype-video-modal"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 101,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'env(safe-area-inset-top, 0) env(safe-area-inset-right, 0) env(safe-area-inset-bottom, 0) env(safe-area-inset-left, 0)',
              outline: 'none',
            }}
          >
            <Dialog.Title
              style={{
                position: 'absolute', width: 1, height: 1, margin: -1,
                padding: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)',
                whiteSpace: 'nowrap', border: 0,
              }}
            >
              I Need Numbers — 45-second hype video
            </Dialog.Title>

            <div
              className="inn-video-shell"
              style={{
                position: 'relative',
                width: '100%',
                maxWidth: 420,
                margin: '0 auto',
                background: '#000',
                borderRadius: 16,
              }}
            >
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Close video"
                  data-testid="inn-hype-video-close-btn"
                  style={{
                    position: 'absolute',
                    top: -14,
                    right: -14,
                    minWidth: 48,
                    height: 48,
                    padding: '0 14px 0 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    borderRadius: 24,
                    background: '#ffffff',
                    border: 'none',
                    color: '#0f172a',
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: 1,
                    cursor: 'pointer',
                    zIndex: 5,
                    fontFamily: 'inherit',
                    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(15, 23, 42, 0.08)',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: '#0f172a',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 700,
                    }}
                  >
                    ×
                  </span>
                  Close
                </button>
              </Dialog.Close>

              <video
                ref={videoRef}
                controls
                playsInline
                preload="metadata"
                poster={INN_HYPE_POSTER_SRC}
                data-testid="inn-hype-video-player"
                onEnded={onEnded}
                style={{
                  display: 'block',
                  width: '100%',
                  maxHeight: '80vh',
                  borderRadius: 16,
                  background: '#000',
                }}
              >
                <source src={INN_HYPE_VIDEO_SRC} type="video/mp4" />
              </video>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

// Poster thumbnail button. Uses the video's poster image, overlays a large
// play-icon circle and a "0:45" duration pill so it's unambiguously a video
// that won't autoplay.
export function INNHypeVideoThumb({
  onClick,
  width = 260,
  aspectRatio = '9 / 16',
  style = {},
  borderRadius = 16,
  testId = 'inn-hype-video-thumb',
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inn-video-thumb"
      aria-label="Watch the 45-second video"
      style={{
        width,
        aspectRatio,
        borderRadius,
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.25)',
        ...style,
      }}
    >
      <img
        src={INN_HYPE_POSTER_SRC}
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          borderRadius,
        }}
      />
      <span className="inn-video-play" aria-hidden>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="#111827">
          <path d="M8 5.14v13.72c0 .79.87 1.27 1.54.84l10.59-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14z" />
        </svg>
      </span>
      <span className="inn-video-duration-pill">0:45</span>
    </button>
  );
}

export default INNHypeVideoModal;
